import {
  DetailsFrame,
  TaskSecondaryAmount,
} from '~/components/shared/RequestDetailsParts';
import trace from '~/lib/trace';
import React, {useState, useEffect} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';
import sy from '~/styles';
import {Text, SlaArticleText, ArticleText} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {service_request as serviceRequestApi} from '~/api/private';
import {isRobTaskApproved, ROB_COST_TEXTS} from '~/types/rob';
import Format from '~/lib/format';
import PropTypes from 'prop-types';
const RequestDetails = ({
  onDismiss,
  damage_id,
  lessor,
  data,
  tasks: request_tasks,
  replacement_rob_code,
  total_amount,
}) => {
  const [replacement, setReplacement] = useState(null);
  const [showUnitPrice, setShowUnitPrice] = useState(false);
  useEffect(() => {
    if (replacement_rob_code) {
      const fetch = async () => {
        const rob_component = Object.values(data?.components ?? []).find(
          (item) => item.rob.code === replacement_rob_code,
        );
        if (rob_component && isRobTaskApproved(rob_component.status.code)) {
          const {result: sla_result} =
            (await serviceRequestApi.sla(damage_id)) ?? {};
          setReplacement({
            rob_component: rob_component,
            sla: sla_result?.codes?.find(
              (item) => item.rob_code === replacement_rob_code,
            ),
          });
        }
      };
      fetch().catch(trace);
    }
  }, [replacement_rob_code, damage_id, data]);

  // Consider moving this helper to a dedicated module.
  const is_fluid = (part) => {
    if (part.attributes?.option_id) {
      return false;
    }
    switch (part.gen_art_nr) {
      case 71: // Remvloeistof
      case 1893: // Ruitenreiniging
      case 3224: // Motorolie
      case 3225: // Versnellingsbakolie
      case 3356: // Koelvloeistof
      case 5040: // Ureum
      case 8561:
        // Versnellingsbakolie
        return true;
      default:
        return false;
    }
  };
  const renderPartAmount = (part, amount_diff, showUnitPrice) => {
    if (showUnitPrice) {
      return (
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {Format.price(
            Format.round(
              part.price ? part.amount / part.quantity : part.amount,
            ),
          )}
          &nbsp;
          {is_fluid(part) ? <>p/l</> : <>p/s</>}
        </Text>
      );
    }
    if (amount_diff !== 0) {
      return (
        <Text
          style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
          {Format.price(part.amount - amount_diff)}
        </Text>
      );
    }
    return null;
  };
  return (
    <DetailsFrame
      onDismiss={onDismiss}
      showUnitPrice={showUnitPrice}
      setShowUnitPrice={setShowUnitPrice}
      total_amount={total_amount}>
      <ScrollView>
        {Object.values(data?.components ?? [])
          .sort((a, b) => a.rob.code?.localeCompare(b.rob.code))
          .map((rob_component) => {
            const main_task = request_tasks.find(
              (item) => item.id === rob_component.id,
            );
            if (!main_task || !isRobTaskApproved(rob_component.status.code)) {
              return null;
            }
            const tasks = [main_task].concat(
              request_tasks.filter(
                (item) =>
                  item.component_id === main_task.component_id &&
                  !item.is_main_task,
              ),
            );
            const parts = tasks
              .filter((task) => task.parts)
              .flatMap((task) => task.parts)
              .filter((part) => part.quantity)
              .flatMap((part) => {
                const {service, ...rest} = part.service_cost ?? {};
                const costs = Object.entries(rest).map(([key, value]) => ({
                  description: ROB_COST_TEXTS[key] ?? key,
                  is_sla: true,
                  quantity: 1,
                  amount: value,
                }));
                return [
                  {
                    ...part,
                    amount:
                      part.quantity * part.price +
                      (part.markup ?? 0) -
                      (part.discount ?? 0),
                  },
                  ...costs,
                ];
              });
            const service_costs = parts.reduce(
              (acc, part) => acc + (part.service_cost?.service ?? 0),
              0,
            );
            return (
              <Grouping
                key={rob_component.id}
                header={
                  <Text style={sy.medium}>
                    {rob_component.component_name ?? (
                      <>
                        {rob_component.rob.code}&nbsp;&middot;&nbsp;
                        {rob_component.rob.description}
                      </>
                    )}
                  </Text>
                }
                initial={{
                  expand: true,
                }}>
                {tasks.map((task) => {
                  let is_sla;
                  let amount;
                  if (isFinite(task.hourly_rate) && isFinite(task.duration)) {
                    is_sla = false;
                    amount = task.hourly_rate * task.duration;
                  } else {
                    is_sla = true;
                    amount = task.amount;
                  }
                  if (task.is_main_task) {
                    amount += service_costs;
                  }
                  amount += task.markup;
                  amount -= task.discount;
                  const amount_diff = is_sla
                    ? task.amount
                    : task.hourly_rate * task.duration;
                  return (
                    <React.Fragment key={task.id}>
                      <View style={[sy['p-4']]}>
                        <View style={[sy['flex-row'], sy['justify-between']]}>
                          <Text style={sy.truncate}>
                            {task.work_text ?? task.description}
                          </Text>
                          <Text style={sy.smallRegular}>
                            {Format.price(Format.round(amount))}
                          </Text>
                        </View>
                        <View style={[sy['flex-row'], sy['justify-between']]}>
                          <View style={[sy['flex-row']]}>
                            <Text
                              style={[sy.smallRegular, sy['text-lightgray']]}>
                              {is_sla ? (
                                <>SLA</>
                              ) : (
                                <>
                                  <>
                                    {Format.number(task.duration, 2)}
                                    &nbsp;u&nbsp;&middot;&nbsp;
                                  </>
                                  {task.reference ? (
                                    <>{task.source ?? 'TecRMI'}</>
                                  ) : (
                                    <>Invoer</>
                                  )}
                                </>
                              )}
                            </Text>
                            {isFinite(
                              task.calculated_work_time ??
                                task.original_work_time,
                            ) &&
                              task.duration !==
                                (task.calculated_work_time ??
                                  task.original_work_time) && (
                                <>
                                  <Text
                                    style={[
                                      sy.smallRegular,
                                      sy['text-lightgray'],
                                    ]}>
                                    &nbsp;(
                                  </Text>
                                  <Text
                                    style={[
                                      sy.smallRegular,
                                      sy['text-lightgray'],
                                      sy['line-through'],
                                    ]}>
                                    {Format.number(
                                      task.calculated_work_time ??
                                        task.original_work_time,
                                      2,
                                    )}
                                    &nbsp;u
                                  </Text>
                                  <Text
                                    style={[
                                      sy.smallRegular,
                                      sy['text-lightgray'],
                                    ]}>
                                    )
                                  </Text>
                                </>
                              )}
                          </View>
                          {
                            <TaskSecondaryAmount
                              showUnitPrice={showUnitPrice}
                              amount={amount}
                              amount_diff={amount_diff}
                              task={task}
                              perItem={is_sla}
                            />
                          }
                        </View>
                      </View>
                      <Divider style={sy['bg-gray95']} />
                    </React.Fragment>
                  );
                })}
                {parts
                  .filter((part) => part.amount > 0)
                  .map((part, index, array) => {
                    const amount_diff = part.price
                      ? part.amount - part.quantity * part.price
                      : 0;
                    return (
                      <React.Fragment key={part.id ?? index}>
                        <View style={[sy['p-4']]}>
                          <View style={[sy['flex-row'], sy['justify-between']]}>
                            <Text style={sy.truncate}>{part.description}</Text>
                            <Text style={sy.smallRegular}>
                              {Format.price(Format.round(part.amount))}
                            </Text>
                          </View>
                          <View style={[sy['flex-row'], sy['justify-between']]}>
                            <Text
                              style={[sy.smallRegular, sy['text-lightgray']]}>
                              {part.quantity}&nbsp;&middot;&nbsp;
                              {part.is_sla ? (
                                <SlaArticleText
                                  lessor={lessor}
                                  attributes={part.attributes}
                                  specifications={part.specifications}
                                  damage_id={damage_id}
                                  itemMpId={part.attributes?.itemMpId}
                                />
                              ) : (
                                <ArticleText
                                  source={part.source}
                                  number={part.number}
                                />
                              )}
                            </Text>
                            {renderPartAmount(part, amount_diff, showUnitPrice)}
                          </View>
                        </View>
                        {index < array.length - 1 && (
                          <Divider style={sy['bg-gray95']} />
                        )}
                      </React.Fragment>
                    );
                  })}
              </Grouping>
            );
          })}
        {replacement && (
          <Grouping
            header={
              <Text style={sy.medium}>
                {replacement.rob_component.rob.code}&nbsp;&middot;&nbsp;
                {replacement.rob_component.rob.description}
              </Text>
            }
            initial={{
              expand: true,
            }}>
            <View style={[sy['flex-row'], sy['p-4'], sy['justify-between']]}>
              <View>
                <Text>{replacement.sla?.item}</Text>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>SLA</Text>
              </View>
              <Text style={sy.smallRegular}>
                {Format.price(replacement.rob_component.price)}
              </Text>
            </View>
          </Grouping>
        )}
      </ScrollView>
    </DetailsFrame>
  );
};
RequestDetails.propTypes = {
  onDismiss: PropTypes.any,
  damage_id: PropTypes.any,
  lessor: PropTypes.any,
  data: PropTypes.any,
  tasks: PropTypes.any,
  replacement_rob_code: PropTypes.any,
  total_amount: PropTypes.any,
};
export default RequestDetails;
